import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineLoading } from "@/components/common/Loading";
import { TransactionRow } from "@/components/wallet/TransactionRow";
import { Money } from "@/components/common/Money";
import { useTransactionsInfinite } from "@/hooks/useTransactions";
import { useUserCoupons } from "@/hooks/useCoupons";
import { useSubscriptions } from "@/hooks/useSubscription";
import { formatDate, formatRelative } from "@/lib/format";
import { PLANS } from "@/lib/constants";

export function History() {
  const { t, i18n } = useTranslation();
  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon"><Link to="/profile"><ArrowLeft /></Link></Button>
        <h1 className="font-display text-xl font-bold">{t("history.title")}</h1>
      </header>

      <Tabs defaultValue="tx">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="tx">{t("history.transactions")}</TabsTrigger>
          <TabsTrigger value="cp">{t("history.coupons")}</TabsTrigger>
          <TabsTrigger value="sub">{t("history.subscriptions")}</TabsTrigger>
        </TabsList>

        <TabsContent value="tx"><TransactionsTab /></TabsContent>
        <TabsContent value="cp"><CouponsTab lang={i18n.language as "fr" | "en"} /></TabsContent>
        <TabsContent value="sub"><SubscriptionsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function TransactionsTab() {
  const { t } = useTranslation();
  const q = useTransactionsInfinite();
  const items = q.data?.pages.flat() ?? [];
  if (q.isLoading) return <InlineLoading />;
  if (items.length === 0) return <EmptyState title={t("common.noData")} />;
  return (
    <div className="space-y-2 mt-3">
      {items.map((tx) => <TransactionRow key={tx.id} tx={tx} />)}
      {q.hasNextPage && (
        <Button variant="outline" className="w-full" loading={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>
          {t("common.seeMore")}
        </Button>
      )}
    </div>
  );
}

function CouponsTab({ lang }: { lang: "fr" | "en" }) {
  const { t } = useTranslation();
  const q = useUserCoupons();
  if (q.isLoading) return <InlineLoading />;
  if (!q.data?.length) return <EmptyState title={t("common.noData")} />;
  return (
    <div className="space-y-2 mt-3">
      {q.data.map((c) => (
        <Link key={c.id} to={`/coupon/${c.id}`}>
          <Card className="hover:bg-accent">
            <CardContent className="py-3 flex items-center justify-between">
              <div>
                <p className="font-bold">{c.type}</p>
                <p className="text-xs text-muted-foreground">
                  {formatRelative(c.created_at, lang)} · {c.correct_count} bons / ?
                </p>
              </div>
              <div className="text-right">
                <StatusBadge status={c.status} />
                {c.payout_amount > 0 && <Money amount={c.payout_amount} className="block mt-1 text-sm font-bold text-success" />}
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

function SubscriptionsTab() {
  const { t } = useTranslation();
  const q = useSubscriptions();
  if (q.isLoading) return <InlineLoading />;
  if (!q.data?.length) return <EmptyState title={t("common.noData")} />;
  return (
    <div className="space-y-2 mt-3">
      {q.data.map((s) => (
        <Card key={s.id}>
          <CardContent className="py-3 flex items-center justify-between">
            <div>
              <p className="font-bold">{PLANS[s.plan].name}</p>
              <p className="text-xs text-muted-foreground">
                {formatDate(s.started_at)} → {formatDate(s.expires_at)}
              </p>
            </div>
            <div className="text-right">
              <Money amount={s.price} className="font-bold" />
              {new Date(s.expires_at) > new Date() ? <Badge variant="success" className="ml-1">Actif</Badge> : <Badge variant="secondary" className="ml-1">Expiré</Badge>}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === "winner" ? "success" : status === "lost" ? "destructive" : status === "refunded" ? "warning" : "secondary";
  const labels: Record<string, string> = { pending: "En cours", winner: "Gagné", lost: "Perdu", refunded: "Remboursé" };
  return <Badge variant={variant as never}>{labels[status] ?? status}</Badge>;
}
